import {
  ImageFilterProps,
  ImageFilterType,
  ImageStrokeEffectProps,
} from "../interfaces";
import { urlToBlob } from "./common";

export const applyFilterToImage = (
  imageSrc: Blob | string,
  filter: ImageFilterProps = { type: ImageFilterType.None },
) => {
  const { type, color } = filter;
  switch (type) {
    case ImageFilterType.Grayscale:
      return applyGrayscale(imageSrc);

    case ImageFilterType.BlendColor:
      return applyBlendColor(imageSrc, { color });

    default:
      return imageSrc;
  }
};

export async function applyGrayscale(imageSrc: Blob | string): Promise<Blob> {
  const inputBlob =
    typeof imageSrc === "string" ? await urlToBlob(imageSrc) : imageSrc;

  let bitmap: ImageBitmap;
  if (typeof createImageBitmap === "function") {
    bitmap = await createImageBitmap(inputBlob);
  } else {
    const img = new Image();
    img.src = URL.createObjectURL(inputBlob);
    await new Promise((resolve, reject) => {
      img.onload = () => resolve(null);
      img.onerror = () => reject(new Error("Image load failed"));
    });

    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = img.naturalWidth;
    tmpCanvas.height = img.naturalHeight;
    const tmpCtx = tmpCanvas.getContext("2d")!;
    tmpCtx.drawImage(img, 0, 0);

    bitmap = await (async () => {
      const b = await new Promise<Blob>((res, rej) =>
        tmpCanvas.toBlob((blob) => (blob ? res(blob) : rej("toBlob failed"))),
      );
      return await createImageBitmap(b);
    })();
  }

  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(bitmap.width, bitmap.height)
      : document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = (canvas as HTMLCanvasElement).getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);

  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const y = 0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!;
    d[i] = d[i + 1] = d[i + 2] = y;
  }
  ctx.putImageData(imgData, 0, 0);

  const mime = inputBlob.type || "image/png";
  if ("convertToBlob" in canvas) {
    return await canvas.convertToBlob({ type: mime });
  } else {
    return await new Promise<Blob>((res, rej) => {
      canvas.toBlob((b) => (b ? res(b) : rej("toBlob failed")), mime);
    });
  }
}

export const applyBlendColor = async (
  imageSrc: Blob | string,
  options?: {
    low?: number;
    high?: number;
    gamma?: number;
    sharpen?: boolean;
    color?: string;
  },
): Promise<Blob | string> => {
  return new Promise(async (resolve, reject) => {
    const worker = new Worker(
      new URL("../workers/BlendColorWorker.ts", import.meta.url),
    );

    worker.onmessage = (event) => {
      if (event.data.error) {
        reject(event.data.error);
      } else {
        const result = event.data.data;
        if (result instanceof ArrayBuffer) {
          const blob = new Blob([result], { type: "image/png" });
          resolve(blob);
        } else {
          resolve(result);
        }
      }
      worker.terminate();
    };

    worker.onerror = (err) => {
      reject(err);
      worker.terminate();
    };

    let buffer: ArrayBuffer;
    if (imageSrc instanceof Blob) {
      buffer = await imageSrc.arrayBuffer();
    } else {
      const blob = await urlToBlob(imageSrc);
      buffer = await blob.arrayBuffer();
    }

    worker.postMessage(
      {
        buffer,
        options: {
          low: 160,
          high: 230,
          gamma: 1.4,
          sharpen: true,
          ...options,
        },
      },
      [buffer],
    );
  });
};

export const applyStrokeEffectToImage = (
  imageSrc: Blob | string,
  strokeEffect: ImageStrokeEffectProps,
) => {
  const { type, color, width } = strokeEffect;
  switch (type) {
    case "Outline":
      return applyOutlineToImage(imageSrc, { color, width });

    default:
      return imageSrc;
  }
};

export const applyOutlineToImage = async (
  imageSrc: Blob | string,
  options: { color: string; width: number },
): Promise<Blob | string> => {
  const outlineColor = options.color;
  const outlineWidth = Math.round(options.width);
  if (!outlineColor || outlineWidth <= 0) return imageSrc;

  return new Promise(async (resolve, reject) => {
    const worker = new Worker(
      new URL("../workers/ImageOutlineWorker.ts", import.meta.url),
    );

    worker.onmessage = (event) => {
      if (event.data.error) {
        reject(event.data.error);
      } else {
        const result = event.data.data;
        if (result instanceof ArrayBuffer) {
          const blob = new Blob([result], { type: "image/png" });
          resolve(blob);
        } else {
          resolve(result);
        }
      }
      worker.terminate();
    };

    worker.onerror = (err) => {
      reject(err);
      worker.terminate();
    };

    let buffer: ArrayBuffer;
    if (imageSrc instanceof Blob) {
      buffer = await imageSrc.arrayBuffer();
    } else {
      const blob = await urlToBlob(imageSrc);
      buffer = await blob.arrayBuffer();
    }

    worker.postMessage({ buffer, options }, [buffer]);
  });
};
